import { filterFns, FilterFn } from "@tanstack/react-table";

/** Non-empty sentinel so TanStack does not auto-remove a blank select filter. */
export const SELECT_EMPTY_FILTER_VALUE = "__EMPTY__";

export const isSelectBlankValue = (value: unknown): boolean =>
  value == null || String(value).trim() === "";

const resolveFilterFn = (filterFn: unknown): FilterFn<any> => {
  if (typeof filterFn === "function") {
    return filterFn as FilterFn<any>;
  }
  if (typeof filterFn === "string" && filterFn in filterFns) {
    return filterFns[filterFn as keyof typeof filterFns];
  }
  return filterFns.includesString;
};

/**
 * Select filters emit a sentinel for blank cells. Wrap the column's existing
 * filterFn so that sentinel matches blanks and every other value keeps the
 * original equals / equalsString / weakEquals behavior.
 */
export const wrapSelectColumnFilterFn = (columnDef: any): any => {
  if (columnDef?.columns) {
    return {
      ...columnDef,
      columns: columnDef.columns.map(wrapSelectColumnFilterFn),
    };
  }
  if (columnDef?.meta?.filterVariant !== "select") {
    return columnDef;
  }
  const originalFn = resolveFilterFn(columnDef.filterFn);
  return {
    ...columnDef,
    filterFn: (row: any, columnId: string, filterValue: unknown, addMeta: any) => {
      if (filterValue === SELECT_EMPTY_FILTER_VALUE) {
        return isSelectBlankValue(row.getValue(columnId));
      }
      return originalFn(row, columnId, filterValue, addMeta);
    },
  };
};

export const wrapSelectColumns = (columns: any[]): any[] =>
  (columns ?? []).map(wrapSelectColumnFilterFn);
